import { Target } from 'lucide-react';

import { MAIN_TABS, SUB_TABS } from './memberNavigation';

import { Skeleton } from '@/components/ui/skeleton';
import useNavFrequency from '@/hooks/useNavFrequency';
import { cn } from '@/lib/utils';

// Build flat registry of all navigable member pages
const buildPageRegistry = () => {
  const pages = [];
  for (const tab of MAIN_TABS) {
    const subs = SUB_TABS[tab.id];
    if (subs) {
      for (const sub of subs) {
        pages.push({
          id: `${tab.id}:${sub.id}`,
          label: sub.label,
          icon: sub.icon,
          tab: tab.id,
          subSection: sub.id,
        });
      }
    } else {
      // Tabs without sub-sections (billing)
      pages.push({
        id: tab.id,
        label: tab.label,
        icon: tab.icon,
        tab: tab.id,
        subSection: null,
      });
    }
  }
  return pages;
};

const PAGE_REGISTRY = buildPageRegistry();

// Default pages for new users (before any frequency data)
const DEFAULT_PAGE_IDS = [
  'progress:calendar',
  'learning:vocabulary',
  'resources:all',
  'billing',
];

// Fixed home item
const HOME_ITEM = {
  id: 'progress:overview',
  label: 'Home',
  icon: Target,
  tab: 'progress',
  subSection: 'overview',
};

const getPageById = (id) => PAGE_REGISTRY.find((p) => p.id === id);

/**
 * Persistent bottom nav for member portal (mobile only).
 * Slot 1: fixed Progress Overview (home)
 * Slots 2-5: top 4 most-visited accessible pages
 */
const MemberMobileBottomNav = ({
  activeTab,
  subSection,
  onTabChange,
  onSubSectionChange,
  canViewBilling = true,
  contentRestricted = false,
  isSectionVisible = () => true,
  isLoaded = true,
  navigationPreferences = {},
  navigationFrequencyStorageKey = null,
}) => {
  const { getTopSections } = useNavFrequency(navigationFrequencyStorageKey);

  // Permission + portal section visibility filter
  const isAccessible = (pageId) => {
    const page = getPageById(pageId);
    if (!page) return false;
    if (navigationPreferences.navigation_hidden?.includes(page.tab) && page.tab !== 'progress') return false;
    // Check portal section visibility first
    if (!isSectionVisible(page.tab)) return false;
    if (page.subSection && !isSectionVisible(`${page.tab}.${page.subSection}`)) return false;
    // Then check permission-based restrictions
    if (page.tab === 'billing') return canViewBilling;
    if (page.tab === 'resources' || page.tab === 'learning') return !contentRestricted;
    return true;
  };

  // Get top 4 (excluding home which is fixed in slot 1)
  let topIds = getTopSections(4, (id) => id !== HOME_ITEM.id && isAccessible(id));

  // Fill with defaults if not enough frequency data
  if (topIds.length < 4) {
    for (const id of DEFAULT_PAGE_IDS) {
      if (topIds.length >= 4) break;
      if (!topIds.includes(id) && id !== HOME_ITEM.id && isAccessible(id)) {
        topIds.push(id);
      }
    }
  }

  const navItems = [
    HOME_ITEM,
    ...topIds.map(getPageById).filter(Boolean),
  ];

  // Determine current page ID
  const currentPageId = subSection
    ? `${activeTab}:${subSection}`
    : activeTab;

  const handleNavClick = (item) => {
    onTabChange(item.tab);
    if (item.subSection && onSubSectionChange) {
      onSubSectionChange(item.subSection);
    }
  };

  return (
    <nav
      aria-label="Member navigation"
      className="fixed bottom-0 left-0 right-0 z-50 h-16 safe-area-pb md:hidden bg-white dark:bg-[#1A1814] border-t border-stone-200 dark:border-stone-700"
    >
      <div className="flex items-stretch h-full">
        {!isLoaded
          ? Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 flex flex-col items-center justify-center gap-1"
                data-testid="bottom-nav-skeleton"
              >
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-2 w-8" />
              </div>
            ))
          : navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === currentPageId;

          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors relative',
                isActive
                  ? 'text-[#C9A227]'
                  : 'text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300'
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className={cn('h-6 w-6', isActive && 'text-[#C9A227]')} />
              <span className="text-[11px] font-medium leading-tight">{item.label}</span>
              {isActive && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#C9A227] rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MemberMobileBottomNav;
